import React from 'react'
import type { Question } from '@/types/quiz'

interface QuestionDisplayProps {
  question: Question
  questionNumber: number
  totalQuestions: number
  selectedAnswer: number | string | undefined
  onAnswerChange: (answer: number | string) => void
}

export const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswer,
  onAnswerChange
}) => {
  const renderQuestionContent = () => {
    switch (question.questionType) {
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <label
                key={index}
                className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedAnswer === index
                    ? 'border-primary bg-primary/10 shadow-lg shadow-primary/10'
                    : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5'
                }`}
              >
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={index}
                  checked={selectedAnswer === index}
                  onChange={() => onAnswerChange(index)}
                  className="mt-1 w-5 h-5 accent-primary flex-shrink-0"
                />
                <span className="text-gray-900 dark:text-white flex-1">{option}</span>
              </label>
            ))}
          </div>
        )

      case 'essay':
        return (
          <div className="space-y-3">
            <textarea
              value={(selectedAnswer as string) || ''}
              onChange={(e) => onAnswerChange(e.target.value)}
              className="w-full min-h-[200px] p-4 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white resize-vertical focus:outline-none focus:border-primary/50"
              placeholder="Type your answer here..."
            />
            <p className="text-sm text-gray-500 dark:text-text-muted flex items-center gap-2">
              <span className="material-symbols-outlined text-[16px]">info</span>
              <span>Essay questions will be graded manually by instructor</span>
            </p>
          </div>
        )

      default:
        return (
          <div className="p-6 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
            <span className="material-symbols-outlined text-red-400 text-3xl mb-2">error</span>
            <p className="text-red-400">Unsupported question type: {question.questionType}</p>
          </div>
        )
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Question Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-bold">{questionNumber}</span>
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 dark:text-text-muted uppercase tracking-wider">
            Question {questionNumber} of {totalQuestions}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
              question.questionType === 'multiple_choice'
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-purple-500/20 text-purple-400'
            }`}>
              {question.questionType === 'multiple_choice' ? 'Multiple Choice' : 'Essay'}
            </span>
            <span className="text-xs text-gray-500 dark:text-text-muted">
              {question.points} {question.points === 1 ? 'point' : 'points'}
            </span>
          </div>
        </div>
      </div>

      {/* Question Text */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-relaxed">
          {question.questionText}
        </h3>
      </div>

      {/* Answer Options */}
      {renderQuestionContent()}
    </div>
  )
}

export default QuestionDisplay
